import { ApiResponse, ApiService } from './api.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import { Place } from '@models/place';
import { SyncStatusService } from './sync-status.service';

@Injectable({
  providedIn: 'root',
})
export class PlaceDataService extends SyncStatusService implements OnDestroy {
  public places: Observable<Place[]>;
  private _places: BehaviorSubject<Place[]>;
  private _loaded = false;
  private data: {
    places: Place[];
    updated: Set<string>;
  };
  private _destroy = new Subject<void>();
  private URL = 'places';

  constructor(private api: ApiService) {
    super();
    this.data = {
      places: [],
      updated: new Set(),
    };
    this._places = new BehaviorSubject([]);
    this.places = this._places.asObservable();
    this._publish();
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._clearSyncTimeout();
  }

  placesExist(): boolean {
    return this.data.places.length > 0;
  }

  byId(id: string): Place {
    return this.data.places.find((p) => p.id === id);
  }

  /**
   * Cached loader for Places
   *
   * @returns {Promise<Array<Place>>}
   * @memberof PlaceDataService
   */
  async load(force = false): Promise<Array<Place>> {
    if (this._loaded && !force) {
      return Promise.resolve([...this.data.places]);
    }
    const dbLoadStart: number = new Date().getTime();
    try {
      const docs: ApiResponse = await this.api.allDocs(this.URL);
      this._lastSync = docs.until;
      this.data.places = docs.results.map((payload) =>
        this._payloadToInstance(payload),
      );
      this._loaded = true;
      this._publish();

      const delta = new Date().getTime() - dbLoadStart;
      const numPlaces = (this.data.places && this.data.places.length) || 0;
      console.debug('Database', delta, 'Load', 'Places');
      console.debug('Num Places', numPlaces);
    } catch (e) {
      console.error(e);
      throw e;
    }
    return this.data.places;
  }

  public loadById(id: string): Promise<Place> {
    return this.api
      .get<Place>(this.URL, id)
      .then((p) => this._payloadToInstance(p));
  }

  public getById(id: string): Place {
    return this.data.places.find((obj) => obj.id === id);
  }

  public delete(place: Place): Promise<void> {
    return this.api
      .delete<Place>(this.URL, place.id)
      .then(() => this._broadcastDelete(place.id));
  }

  public unload(): void {
    'Logged out then clear places';
    this.data.places = [];
    this._publish();
  }

  public async createOrUpdate(place: Place): Promise<Place> {
    let result, updated: Place;
    try {
      result = await this.api.put<Place>(
        this.URL,
        place.id,
        place,
        place.updated,
      );
      updated = this._addUpdate(result);
      this._publish();
    } catch (e) {
      if (e.status !== 409) {
        throw e;
      }
      // 409 is a conflict, meaning the doc was updated on the server
      // reload it and rethrow exception so user can be informed.
      const obj = await this.api.get<Place>(this.URL, place.id);
      updated = this._addUpdate(obj);
      this._publish();
      throw e;
    }
    return updated;
  }

  protected async _getLatestFromApi(): Promise<Place[]> {
    const docs: ApiResponse = await this.api.allDocs(this.URL, this._lastSync);
    this._lastSync = docs.until;
    return docs.results;
  }

  protected _publish(): void {
    // new object list to trigger change detection and isolate changes.
    if (!this._places.closed) {
      this._places.next([...this.data.places]);
    }
  }

  protected _addUpdate(payload: Place) {
    const place: Place = this._payloadToInstance(payload);
    this._processChange(place);
    return place;
  }

  private _payloadToInstance(place: any): Place {
    if (typeof place.width === 'string') {
      place.width = parseFloat(place.width);
    }
    if (typeof place.height === 'string') {
      place.height = parseFloat(place.height);
    }
    if (typeof place.gridspacing === 'string') {
      place.gridspacing = parseFloat(place.gridspacing);
    }
    return new Place({ ...place });
  }

  private _processChange(place: Place): Place {
    this.data.places = [
      ...this.data.places.filter((p) => p.id !== place.id),
      place,
    ];
    return place;
  }

  private _broadcastDelete(id: string): void {
    this.data.places = this.data.places.filter((p) => p.id !== id);
    this._publish();
  }
}
